import React from 'react';
import { useNavigate } from 'react-router-dom';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { GAME_CONFIG } from './config/gameConfig';
import { useScrabbleGameContext } from './context/ScrabbleGameContext';
import { createGlimmerButtonSx } from '../../utils/glimmerButtonSx';

const glimmerButtonSx = createGlimmerButtonSx({
  animationName: 'victoryButtonGlimmer',
  duration: 2.4,
});

function VictoryOverlay() {
  const navigate = useNavigate();
  const {
    runPoints,
    roundIndex,
    clearGameState,
    continueInfiniteMode,
  } = useScrabbleGameContext();

  const handleReturnToMenu = React.useCallback(() => {
    clearGameState();
    navigate('/');
  }, [clearGameState, navigate]);


  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 980,
        borderRadius: 3,
        border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
        backgroundColor: 'background.paper',
        padding: { xs: 1.5, md: 2.5 },
        boxShadow: (theme) =>
          `0 10px 40px ${alpha(theme.palette.warning.main, 0.22)}, 0 2px 12px ${alpha(theme.palette.common.black, 0.12)}`,
        background: (theme) =>
          `linear-gradient(145deg, ${alpha(theme.palette.warning.light, 0.18)} 0%, ${alpha(
            theme.palette.background.paper,
            0.97
          )} 60%)`,
      }}
    >
      <Stack spacing={2} alignItems="center">
        <Stack spacing={1} alignItems="center">
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              border: (theme) => `2px solid ${alpha(theme.palette.warning.main, 0.5)}`,
              background: (theme) =>
                `radial-gradient(circle, ${alpha(theme.palette.warning.light, 0.3)} 0%, ${alpha(
                  theme.palette.warning.main,
                  0.1
                )} 100%)`,
              display: 'grid',
              placeItems: 'center',
              boxShadow: (theme) => `0 0 28px ${alpha(theme.palette.warning.main, 0.3)}`,
            }}
          >
            <EmojiEventsRoundedIcon sx={{ fontSize: 38, color: 'warning.main' }} />
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              letterSpacing: -0.5,
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            You Won Altessera!
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
            You cleared all {GAME_CONFIG.maxRounds} rounds. An incredible run.
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
          <Chip
            icon={<EmojiEventsRoundedIcon sx={{ fontSize: 16 }} />}
            label={`${roundIndex} Rounds Cleared`}
            color="warning"
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
          <Chip
            label={`${Number(runPoints || 0).toLocaleString()} Run Points`}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%', maxWidth: 480 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AllInclusiveRoundedIcon />}
            onClick={continueInfiniteMode}
            sx={glimmerButtonSx}
          >
            Keep Playing
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<HomeRoundedIcon />}
            onClick={handleReturnToMenu}
          >
            Return to Menu
          </Button>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', opacity: 0.7 }}>
          Keep Playing continues in infinite mode with no round cap.
        </Typography>
      </Stack>
    </Box>
  );
}

export default VictoryOverlay;
